import { agentToolCatalog } from "../../autogen/agentToolCatalog";
import { TOOL_MODULE_NOT_INCLUDED, toolModuleNotIncluded } from "./enabledTools";
import { bffApi, type Regulation } from "../../autogen/bff-api";
import type { AgentToolDescriptor, AgentToolParam } from "./agentTypes";
import type { AppDispatch } from "../redux/store";
import { withAgentToolHeader } from "./agentToolHeader";
import { isNotFound, PROCUREMENT_GONE_RESULT, procurementIsGone } from "./procurementGone";
import { withOriginatingNeed } from "./originatingNeed";
import i18next from "i18next";
import {
  contractArgAdapters,
  contractContextChecks,
  contractContextInjections,
} from "./contractToolAdapters";
import { projectContextChecks, projectContextInjections } from "./projectToolAdapters";
import { sourcingArgAdapters, sourcingContextChecks } from "./sourcingToolAdapters";
import { READINESS_TOOL_NAME, readinessForModel } from "./readinessForModel";
import {
  STEP_DOCUMENT_PART_TOOL_NAME,
  STEP_DOCUMENT_TOOL_NAME,
  stepDocumentPartRequest,
  stepDocumentRequest,
  writeStepDocument,
  writeStepDocumentPart,
} from "./stepDocument";

export type GenerateDraft = (args: Record<string, unknown>, signal?: AbortSignal) => Promise<ToolResult>;

export type ToolResult =
  | { ok: true; data: unknown }
  | {
      ok: false;
      deviationCode: string;
      message: string;
      cardFieldsNow?: unknown;
      changedSinceYourLastRead?: unknown;
      storedRules?: unknown;
      current?: unknown;
      storedAtRefusal?: Record<string, unknown>;
    };

export type ToolContext = {
  dispatch: AppDispatch;
  auth: {
    organizationId: string;
    personId: string;
    departmentId: string;
    eventDepartments: { id: string; name: string }[];
    contractDepartments: { id: string; name: string }[];
    projectDepartments?: { id: string; name: string }[];
    defaultProjectDepartmentId?: string;
  };
  // Norway's country id from baseData — the default for entities the assistant creates.
  defaultCountryId: string;
  buyerRunsPublicProcurements?: boolean;
  buyerRegulation?: Regulation | null;
  newProcurementProjectId?: string;
  createsProcurementWithAi?: boolean;
  regulationPending?: () => boolean;
};

export const SEARCH_TOOL_NAME = "search_tender_documents";
export const DRAFT_TOOL_NAME = "draft_content";
export const OPEN_DOCUMENT_TOOL_NAME = "open_document";

// Surface the real failure to the planner so it can self-correct — including the raw body of a
// PARSING_ERROR (a non-JSON 500 body, e.g. a server validation message).
export const toError = (err: unknown): ToolResult => {
  const e = err as { status?: number | string; originalStatus?: number; data?: unknown; error?: string };
  const status = String(e?.originalStatus ?? e?.status ?? "UNKNOWN");
  const data = e?.data;
  if (data && typeof data === "object") {
    const d = data as { errorCode?: string; message?: string; fieldErrors?: FieldError[] };
    if (d.errorCode === TOOL_MODULE_NOT_INCLUDED) {
      return toolModuleNotIncluded;
    }
    const fieldErrors = d.fieldErrors ?? [];
    if (fieldErrors.length > 0) {
      const stored = storedAtRefusal(fieldErrors);
      return {
        ok: false,
        deviationCode: fieldErrors[0].code,
        message: fieldErrorsMessage(fieldErrors),
        ...(stored ? { storedAtRefusal: stored } : {}),
      };
    }
    const explained = d.errorCode ? (API_ERROR_EXPLANATIONS[d.errorCode] ?? plainReason(d.errorCode)) : undefined;
    return {
      ok: false,
      deviationCode: d.errorCode ?? status,
      message: explained ?? d.message ?? d.errorCode ?? JSON.stringify(data),
    };
  }
  const raw = typeof data === "string" && data.trim() ? data.trim() : e?.error;
  return { ok: false, deviationCode: status, message: raw ? `${status}: ${raw}` : "Forespørselen feilet." };
};

type FieldError = { field: string; code: string; params?: Record<string, string> };

const RULES_AT_REFUSAL: Record<string, string> = {
  storedRegulation: "publicContext.regulation",
  storedFoaPart: "publicContext.foaPart",
  storedProcedureType: "publicContext.procedureType",
};

const storedAtRefusal = (fieldErrors: FieldError[]): Record<string, unknown> | undefined => {
  const refusals = fieldErrors.flatMap(({ field, code, params }) =>
    code === "zz_FieldErrorCode_ChangedSinceProposed" && params?.stored !== undefined ? [{ field, params }] : [],
  );
  if (refusals.length === 0) return undefined;
  const rulesParams = refusals[0].params;
  const rules = Object.entries(RULES_AT_REFUSAL).flatMap(([param, field]) =>
    rulesParams[param] !== undefined ? [[field, JSON.parse(rulesParams[param])] as const] : [],
  );
  return Object.fromEntries([...rules, ...refusals.map(({ field, params }) => [field, JSON.parse(params.stored)] as const)]);
};

const FIELD_ERROR_EXPLANATIONS: Record<string, (field: string) => string> = {
  zz_FieldErrorCode_ChangedSinceProposed: (field) =>
    `${field} er endret et annet sted etter at kortet ble laget, så kortet ville skrevet over den endringen. ` +
    "Si det til brukeren, beskriv feltene bare slik «cardFieldsNow» i dette svaret viser dem, og tilby et nytt " +
    "forslag bygget på den.",
  zz_FieldErrorCode_ImmutableInStatus: (field) =>
    `${field} kan ikke endres lenger, fordi anskaffelsen er publisert. Forklar brukeren det, og IKKE prøv igjen.`,
  zz_FieldErrorCode_NotInThisKindOfProcurement: (field) =>
    `${field} finnes ikke i denne typen anskaffelse, så det ble ikke lagret. Si det til brukeren.`,
};

function unreadableDocument(reason: string): string {
  return (
    `Dokumentet kan ikke leses. ${reason}. Si det til brukeren, og foreslå å åpne filen eller laste opp en ny ` +
    "versjon. Si ALDRI at det er midlertidig, og foreslå ikke å prøve igjen senere."
  );
}

const plainReason = (code: string, params: Record<string, string> = {}): string | undefined =>
  i18next.exists(code, { lng: MODEL_LANGUAGE }) ? String(i18next.t(code, { ...params, lng: MODEL_LANGUAGE })) : undefined;

export const MODEL_LANGUAGE = "no";

const API_ERROR_EXPLANATIONS: Record<string, string> = {
  zz_SourcingEventApiErrorCode_AwardCriteriaMustBeExactlyOneType:
    "Anskaffelsen kan ha bare én tildelingsmodell: enten laveste pris eller beste forhold mellom pris og " +
    "kvalitet, aldri begge. Pris er et kriterium inne i beste forhold mellom pris og kvalitet. Send bare " +
    "bestPriceQualityRatio med alle kriteriene, pris medregnet, eller bare lowestPrice.",
  zz_SourcingEventApiErrorCode_NotEditableInStatus:
    "Anskaffelsen kan ikke endres lenger i statusen den har nå, for eksempel fordi den er " +
    "tildelt, avsluttet eller avlyst. Forklar brukeren det, og IKKE prøv igjen.",
  zz_DocumentApiErrorCode_DocumentUnreadable: unreadableDocument("Filen kan være skadet eller uten tekst"),
  zz_DocumentApiErrorCode_DocumentScanned: unreadableDocument("Filen ser ut til å være skannet, så teksten kan ikke leses"),
  zz_DocumentApiErrorCode_DocumentHasNoText: unreadableDocument("Det finnes ingen tekst i filen"),
};

const fieldErrorsMessage = (fieldErrors: FieldError[]): string =>
  fieldErrors
    .map(
      ({ field, code, params }) =>
        FIELD_ERROR_EXPLANATIONS[code]?.(field) ?? `${field}: ${plainReason(code, params) ?? code}.`,
    )
    .join(" ");

type EndpointInitiator = { initiate: (arg: Record<string, unknown>) => unknown };

// An adapter fully assembles the request for an endpoint the OpenAPI spec can't describe as
// model-facing params. The domain registry it lives in is composed in via the import above.
export type ArgAdapter = (args: Record<string, unknown>, ctx: ToolContext) => Record<string, unknown>;

// Rejects a call the schema alone cannot judge, because the answer depends on client context.
export type ContextCheck = (args: Record<string, unknown>, ctx: ToolContext) => ToolResult | null;

// A declarative injection for endpoints whose real request carries client context the model must not
// supply. The engine folds the catalog's advertised body params under bodyKey, adds the context
// defaults, and sets the path params — no per-tool imperative code.
export type ContextInjection = {
  bodyKey: string;
  pathParams?: Record<string, (ctx: ToolContext, args: Record<string, unknown>) => unknown>;
  bodyDefaults?: Record<string, (ctx: ToolContext, args: Record<string, unknown>) => unknown>;
  // Advertised params the endpoint's request body has no field for — they only feed pathParams.
  excludeFromBody?: string[];
};

const ARG_ADAPTERS: Record<string, ArgAdapter> = {
  ...contractArgAdapters,
  ...sourcingArgAdapters,
  [STEP_DOCUMENT_TOOL_NAME]: stepDocumentRequest,
  [STEP_DOCUMENT_PART_TOOL_NAME]: stepDocumentPartRequest,
};
const CONTEXT_CHECKS: Record<string, ContextCheck> = {
  ...sourcingContextChecks,
  ...contractContextChecks,
  ...projectContextChecks,
};
const CONTEXT_INJECTIONS: Record<string, ContextInjection> = { ...contractContextInjections, ...projectContextInjections };

export const wrappedBodyKey = (operationId: string): string | undefined => CONTEXT_INJECTIONS[operationId]?.bodyKey;

export const withBodyFolded = (
  descriptor: AgentToolDescriptor,
  args: Record<string, unknown>,
): { descriptor: AgentToolDescriptor; args: Record<string, unknown> } => {
  const injection = CONTEXT_INJECTIONS[descriptor.toolName];
  if (!injection) return { descriptor, args };
  const excluded = new Set(injection.excludeFromBody ?? []);
  const folded = descriptor.params.filter((param) => param.in === "body" && !excluded.has(param.name));
  if (folded.length === 0) return { descriptor, args };
  const foldedNames = new Set(folded.map((param) => param.name));
  const body = foldedBody(injection, [...foldedNames], args);
  const rest = Object.fromEntries(
    Object.entries(args).filter(([name]) => !foldedNames.has(name) && name !== injection.bodyKey),
  );
  const bodyParam: AgentToolParam = {
    name: injection.bodyKey,
    in: "body",
    required: true,
    schema: {
      type: "object",
      properties: Object.fromEntries(folded.map((param) => [param.name, param.schema])),
      required: folded.filter((param) => param.required).map((param) => param.name),
    },
  };
  return {
    descriptor: { ...descriptor, params: [...descriptor.params.filter((param) => !foldedNames.has(param.name)), bodyParam] },
    args: { ...rest, [injection.bodyKey]: body },
  };
};

const descriptorFor = (operationId: string) => agentToolCatalog.find((tool) => tool.toolName === operationId);

const bodyParamNames = (operationId: string): string[] =>
  descriptorFor(operationId)?.params.filter((p) => p.in === "body").map((p) => p.name) ?? [];

const foldedBody = (
  injection: ContextInjection,
  bodyParams: string[],
  args: Record<string, unknown>,
): Record<string, unknown> => {
  const nested = args[injection.bodyKey];
  const nestedBody = nested && typeof nested === "object" ? (nested as Record<string, unknown>) : {};
  const excluded = new Set(injection.excludeFromBody ?? []);
  return Object.fromEntries(
    bodyParams.flatMap((name) => {
      const value = args[name] ?? nestedBody[name];
      return excluded.has(name) || value === undefined ? [] : [[name, value]];
    }),
  );
};

const applyContextInjection = (
  operationId: string,
  injection: ContextInjection,
  args: Record<string, unknown>,
  ctx: ToolContext,
): Record<string, unknown> => {
  const body = foldedBody(injection, bodyParamNames(operationId), args);
  for (const [name, produce] of Object.entries(injection.bodyDefaults ?? {})) {
    body[name] = produce(ctx, args);
  }
  const result: Record<string, unknown> = { [injection.bodyKey]: body };
  for (const [name, produce] of Object.entries(injection.pathParams ?? {})) {
    result[name] = produce(ctx, args);
  }
  return result;
};

// Assemble the real request from the model's args and the client context: a bespoke adapter for the
// endpoints that need one, a declarative context injection for the rest, else the args verbatim.
export const buildEndpointArgs = (
  operationId: string,
  args: Record<string, unknown>,
  ctx: ToolContext,
): Record<string, unknown> => {
  const adapter = ARG_ADAPTERS[operationId];
  if (adapter) {
    return adapter(args, ctx);
  }
  const injection = CONTEXT_INJECTIONS[operationId];
  if (injection) {
    return applyContextInjection(operationId, injection, args, ctx);
  }
  return args;
};

// Deviations mirror the ones runTool's own callers (e.g. generateDraft's MISSING_INSTRUCTION) return
// on bad input, so the planner sees a tool result it can self-correct from rather than a silent
// dispatch of degenerate data.
const missingFieldError = (name: string): ToolResult => ({
  ok: false,
  deviationCode: "MISSING_FIELD",
  message: `Feltet '${name}' er påkrevd — fyll det ut før verktøyet kjøres.`,
});

// A blank string on a field the schema marks minLength >= 1 would silently clear the field
// server-side (the ConfirmationCard's hasValue() treats a blank as "not present" and shows nothing
// about it), so reject it before it reaches the endpoint.
const blankFieldError = (name: string): ToolResult => ({
  ok: false,
  deviationCode: "BLANK_FIELD",
  message: `Feltet '${name}' kan ikke være tomt — utelat felt som ikke skal endres.`,
});

const isBlankString = (value: unknown, schema: Record<string, unknown>): boolean =>
  typeof value === "string" &&
  typeof schema.minLength === "number" &&
  schema.minLength >= 1 &&
  value.trim().length === 0;

// Resolve a param's value from the model's args, tolerating a body param the model nested under the
// injection's bodyKey rather than supplying flat (the shape the catalog advertises).
const paramValue = (
  args: Record<string, unknown>,
  param: AgentToolParam,
  bodyKey: string | undefined,
): unknown => {
  const direct = args[param.name];
  if (direct !== undefined) {
    return direct;
  }
  if (param.in === "body" && bodyKey) {
    const nested = args[bodyKey];
    if (nested && typeof nested === "object") {
      return (nested as Record<string, unknown>)[param.name];
    }
  }
  return undefined;
};

const missingObjectFieldError = (value: Record<string, unknown>, schema: Record<string, unknown>): ToolResult | null => {
  const required = schema.required as string[] | undefined;
  for (const name of required ?? []) {
    if (value[name] === undefined || value[name] === null) {
      return missingFieldError(name);
    }
  }
  return null;
};

// Walk a present object's leaves against its schema and reject any blank string whose schema demands
// a minimum length. Required is enforced only at the object's top level (by the caller) so a
// legitimate partial update of a nested object is not mistaken for a missing field.
const blankLeafError = (value: unknown, schema: Record<string, unknown>): ToolResult | null => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  const properties = schema.properties as Record<string, Record<string, unknown>> | undefined;
  if (!properties) {
    return null;
  }
  for (const [name, leaf] of Object.entries(value as Record<string, unknown>)) {
    const leafSchema = properties[name];
    if (!leafSchema) {
      continue;
    }
    if (isBlankString(leaf, leafSchema)) {
      return blankFieldError(name);
    }
    const nested = blankLeafError(leaf, leafSchema);
    if (nested) {
      return nested;
    }
  }
  return null;
};

// Validate the model's args against the tool's real JSON Schema in the catalog: required params must
// be present and any present string the schema marks minLength >= 1 must be non-blank. Purely
// schema-driven — no per-tool code. The executor injects auth/context params, which the catalog does
// not advertise, so they are never flagged.
export const validateToolArgs = (operationId: string, args: Record<string, unknown>): ToolResult | null => {
  const descriptor = descriptorFor(operationId);
  if (!descriptor) {
    return null;
  }
  const bodyKey = CONTEXT_INJECTIONS[operationId]?.bodyKey;
  for (const param of descriptor.params) {
    const value = paramValue(args, param, bodyKey);
    if (param.required && (value === undefined || value === null)) {
      return missingFieldError(param.name);
    }
    if (value === undefined || value === null) {
      continue;
    }
    if (param.schema.type === "object" && value && typeof value === "object" && !Array.isArray(value)) {
      const missing = missingObjectFieldError(value as Record<string, unknown>, param.schema);
      if (missing) {
        return missing;
      }
      const blank = blankLeafError(value, param.schema);
      if (blank) {
        return blank;
      }
    } else if (isBlankString(value, param.schema)) {
      return blankFieldError(param.name);
    }
  }
  return null;
};

// Generic execution: any catalog tool is just an operationId. Auth/org/person context is injected
// here; the model only supplies the domain params advertised by the catalog. buildEndpointArgs shapes
// the request for the rare endpoint that needs it.
export const runEndpointTool = async (
  operationId: string,
  args: Record<string, unknown>,
  ctx: ToolContext,
): Promise<ToolResult> => {
  const endpoints = bffApi.endpoints as unknown as Record<string, EndpointInitiator>;
  const endpoint = endpoints[operationId];
  if (!endpoint) {
    return { ok: false, deviationCode: "UNKNOWN_TOOL", message: `Ukjent verktøy: ${operationId}` };
  }
  const validationError = validateToolArgs(operationId, args);
  if (validationError) {
    return validationError;
  }
  const contextError = CONTEXT_CHECKS[operationId]?.(args, ctx) ?? null;
  if (contextError) {
    return contextError;
  }
  try {
    const data = await dispatchEndpoint(endpoint, operationId, args, ctx);
    return { ok: true, data };
  } catch (err) {
    return (await refusedForMissingProcurement(err, args, ctx)) ?? toError(err);
  }
};

const refusedForMissingProcurement = async (
  err: unknown,
  args: Record<string, unknown>,
  ctx: ToolContext,
): Promise<ToolResult | null> => {
  const eventId = typeof args.eventId === "string" ? args.eventId : null;
  if (!eventId || !isNotFound(err)) return null;
  return (await procurementIsGone(eventId, ctx)) ? PROCUREMENT_GONE_RESULT : null;
};

const dispatchEndpoint = (
  endpoint: EndpointInitiator,
  operationId: string,
  args: Record<string, unknown>,
  ctx: ToolContext,
): Promise<unknown> => {
  const authContext = {
    organizationId: ctx.auth.organizationId,
    personId: ctx.auth.personId,
    orgId: ctx.auth.organizationId,
  };
  const finalArgs = buildEndpointArgs(operationId, args, ctx);
  return withAgentToolHeader(operationId, () => {
    const thunk = endpoint.initiate({ ...authContext, ...finalArgs });
    const dispatched = ctx.dispatch(thunk as never) as unknown as { unwrap: () => Promise<unknown> };
    return dispatched.unwrap();
  });
};

export type RefusalReason = { field?: string; code: string; params?: Record<string, string> };

export type ProposalCheck =
  | { refused: false }
  | { refused: true; result: Extract<ToolResult, { ok: false }>; reasons: RefusalReason[] };

export const checkProposal = async (
  operationId: string,
  args: Record<string, unknown>,
  ctx: ToolContext,
): Promise<ProposalCheck> => {
  const endpoint = (bffApi.endpoints as unknown as Record<string, EndpointInitiator>)[operationId];
  if (!endpoint) {
    return { refused: false };
  }
  try {
    await dispatchEndpoint(endpoint, operationId, { ...args, dryRun: true }, ctx);
    return { refused: false };
  } catch (err) {
    const reasons = refusalReasonsOf(err);
    const result = toError(err);
    return reasons.length > 0 && !result.ok ? { refused: true, result, reasons } : { refused: false };
  }
};

const refusalReasonsOf = (err: unknown): RefusalReason[] => {
  const e = err as { status?: unknown; data?: unknown };
  const isRefusal = typeof e?.status === "number" && e.status >= 400 && e.status < 500;
  if (!isRefusal || !e.data || typeof e.data !== "object") {
    return [];
  }
  const d = e.data as { errorCode?: string; fieldErrors?: FieldError[] };
  if (d.fieldErrors && d.fieldErrors.length > 0) {
    return d.fieldErrors.map(({ field, code, params }) => ({ field, code, ...(params ? { params } : {}) }));
  }
  return d.errorCode ? [{ code: d.errorCode }] : [];
};

export const runTool = async (
  toolName: string,
  args: Record<string, unknown>,
  ctx: ToolContext,
  generateDraft: GenerateDraft,
  signal?: AbortSignal,
): Promise<ToolResult> => {
  if (toolName === DRAFT_TOOL_NAME) {
    return generateDraft(args, signal);
  }
  if (toolName === STEP_DOCUMENT_TOOL_NAME) {
    return writeStepDocument(args, generateDraft, (withText) => runEndpointTool(toolName, withText, ctx), signal);
  }
  if (toolName === STEP_DOCUMENT_PART_TOOL_NAME) {
    return writeStepDocumentPart(args, generateDraft, ctx, (withText) => runEndpointTool(toolName, withText, ctx), signal);
  }
  if (toolName === "getSourcingEvent") {
    return withOriginatingNeed(await runEndpointTool(toolName, args, ctx), args.eventId, ctx);
  }
  if (toolName === READINESS_TOOL_NAME) {
    return readinessForModel(await runEndpointTool(toolName, args, ctx));
  }
  return runEndpointTool(toolName, args, ctx);
};

export const PLANNER_RESULT_MAX_CHARS = 6000;

export const READ_CONVERSATION_FILE_TOOL_NAME = "readConversationFile";
export const READ_DOCUMENT_TEXT_TOOL_NAME = "readDocumentText";

export const readsDocumentText = (toolName: string | undefined): boolean =>
  toolName === READ_CONVERSATION_FILE_TOOL_NAME || toolName === READ_DOCUMENT_TEXT_TOOL_NAME;

export const keepsWholeResult = (toolName: string | undefined): boolean =>
  readsDocumentText(toolName) || toolName === DRAFT_TOOL_NAME || toolName === READINESS_TOOL_NAME;

const ONE_RECORD_TOOLS = new Set(["getContract", "getSourcingEvent", "updateSourcingEvent"]);
export const ONE_RECORD_RESULT_MAX_CHARS = 16000;

export const resultMaxCharsFor = (toolName: string | undefined): number =>
  toolName && ONE_RECORD_TOOLS.has(toolName) ? ONE_RECORD_RESULT_MAX_CHARS : PLANNER_RESULT_MAX_CHARS;

export const boundForPlanner = (result: ToolResult, toolName?: string): string => {
  const json = result.ok ? JSON.stringify(result.data) : JSON.stringify(result);
  const maxChars = resultMaxCharsFor(toolName);
  if (json.length <= maxChars || keepsWholeResult(toolName)) {
    return json;
  }
  return `${json.slice(0, maxChars)}… [avkortet: svaret var ${json.length} tegn, bare de første ${maxChars} vises her]`;
};
